import React from 'react';
import { useWorkspacePath } from 'pages/workspaces/useWorkspace';
import { basePaths } from 'app-constants';
import { useQueryAndHash } from './useQueryAndHash';
import { LinkHelperProps } from './index';
import { TrackedLink } from 'components/common/TrackedLink';

interface SecondarySourceLinkProps extends LinkHelperProps {
  practiceAreaSlug?: string;
  productCode?: string;
  onlawId?: string;
  id?: string;
  documentPartId?: string;
  refglobal?: string;
}

export type { SecondarySourceLinkProps };

export function useSecondarySourcePath(
  practiceAreaSlug?: string,
  productCode?: string,
  onlawId?: string,
  query?: string | null,
  hash?: string | null,
) {
  const getPath = useGetSecondarySourcePath();
  const path = React.useMemo(
    () => getPath(practiceAreaSlug, productCode, onlawId),
    [getPath, practiceAreaSlug, productCode, onlawId],
  );

  return useQueryAndHash(path, query, hash);
}

export function useGetSecondarySourcePath() {
  const workspacePath = useWorkspacePath();

  return React.useCallback(
    (practiceAreaSlug?: string, productCode?: string, onlawId?: string) => {
      const parts = [workspacePath, basePaths.secondarySources];

      if (practiceAreaSlug || productCode || onlawId) {
        parts.push('area');
        if (practiceAreaSlug) parts.push(practiceAreaSlug);
        if (productCode) parts.push(productCode);
        if (onlawId) parts.push(onlawId);
      }

      return parts
        .filter(Boolean)
        .map((str, i) => (i > 0 ? str.replace(/^\//, '') : str))
        .join('/')
        .replace(/\/+$/, '');
    },
    [workspacePath],
  );
}

export function parseSecondarySourcePath(path: string) {
  // Parse secondary source paths like /secondary-sources/{id}
  const secondarySourcePathRegex = new RegExp(
    `^${basePaths.secondarySources}/([^/]+)$`,
  );
  const match = path.match(secondarySourcePathRegex);
  if (match) {
    return {
      id: match[1],
    };
  }
  return null;
}

export function SecondarySourceLink({
  to,
  practiceAreaSlug,
  productCode,
  onlawId,
  query,
  hash,
  ...props
}: SecondarySourceLinkProps) {
  const path = useSecondarySourcePath(
    practiceAreaSlug,
    productCode,
    onlawId,
    query,
    hash,
  );
  return <TrackedLink to={to ? to : path} {...props} />;
}
